// Tiptap's shortcut format is `Mod-Shift-B`, `Mod-Alt-1`, etc. `Mod` resolves to ⌘ on
// Mac and Ctrl elsewhere. This util renders those for display in tooltips.

const MAC_KEYS: Record<string, string> = {
  Mod: '⌘',
  Shift: '⇧',
  Alt: '⌥',
  Ctrl: '⌃',
};

const PC_KEYS: Record<string, string> = {
  Mod: 'Ctrl',
  Shift: 'Shift',
  Alt: 'Alt',
  Ctrl: 'Ctrl',
};

export const isMacPlatform = (): boolean => {
  if (typeof navigator === 'undefined') {
    return false;
  }

  return /Mac|iPhone|iPad|iPod/.test(navigator.platform);
};

export const formatShortcut = (shortcut: string, isMac: boolean = isMacPlatform()): string => {
  const keys = isMac ? MAC_KEYS : PC_KEYS;
  const separator = isMac ? '' : '+';
  return shortcut
    .split('-')
    .map((key) => keys[key] ?? key.toUpperCase())
    .join(separator);
};

/**
 * Whether a key event is the given shortcut, in the same `Mod-Shift-B` format `formatShortcut` renders.
 * A modifier the shortcut does not name must be absent, so `Mod-B` ignores `Mod-Shift-B`.
 */
export const matchesShortcut = (event: KeyboardEvent, shortcut: string): boolean => {
  const parts = shortcut.split('-');
  const key = parts.at(-1) ?? '';
  const modifiers = new Set(parts.slice(0, -1));

  if (event.key.toLowerCase() !== key.toLowerCase()) {
    return false;
  }

  if (modifiers.has('Shift') !== event.shiftKey || modifiers.has('Alt') !== event.altKey) {
    return false;
  }

  if (modifiers.has('Ctrl')) {
    return event.ctrlKey && !event.metaKey;
  }
  if (modifiers.has('Mod')) {
    return event.metaKey !== event.ctrlKey;
  }
  return !event.metaKey && !event.ctrlKey;
};
